import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { SvgRendererLogic } from './logic'
import { SvgRendererProps, SvgRendererSize } from './types'

const SIZE_MAP: Record<SvgRendererSize, { width: string; height: string }> = {
    'extra small': { width: '16px', height: '16px' },
    small: { width: '20px', height: '20px' },
    medium: { width: '24px', height: '24px' },
    large: { width: '32px', height: '32px' },
    'extra large': { width: '48px', height: '48px' },
}

export function SvgRenderer({
    src,
    size,
    width,
    height,
    strokeColor,
    fillColor,
    alt = '',
    className,
    style,
    onLoad,
    onError,
    ...rest
}: SvgRendererProps) {
    const { logic, state } = useLogic(SvgRendererLogic, {
        src,
        strokeColor,
        fillColor,
        onLoad,
        onError,
    })

    // Load SVG when src changes
    useEffect(() => {
        if (src) {
            logic.loadSvg(src)
        }
    }, [src])

    // Process SVG content with colors/gradients
    const processedSvgContent = state.svgContent
        ? logic.processSvg(state.svgContent, strokeColor, fillColor)
        : null

    // Determine final dimensions
    const customWidth = typeof width === 'number' ? `${width}px` : width
    const customHeight = typeof height === 'number' ? `${height}px` : height

    let dimensions
    if (customWidth || customHeight) {
        dimensions = {
            width: customWidth || 'auto',
            height: customHeight || 'auto',
        }
    } else if (size) {
        dimensions = {
            width: SIZE_MAP[size].width,
            height: SIZE_MAP[size].height,
        }
    } else {
        dimensions = {
            width: '24px',
            height: '24px',
        }
    }

    if (state.error) {
        return (
            <ErrorContainer
                className={className}
                style={{ ...style, ...dimensions }}
                {...rest}
            >
                <ErrorText>!</ErrorText>
            </ErrorContainer>
        )
    }

    if (!processedSvgContent) {
        return null
    }

    return (
        <SvgContainer
            className={className}
            style={{ ...style, ...dimensions }}
            dangerouslySetInnerHTML={{ __html: processedSvgContent }}
            {...rest}
        />
    )
}

// Styled Components
const BaseContainer = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`

const SvgContainer = styled(BaseContainer).withConfig({
    shouldForwardProp: (prop) => !['width', 'height'].includes(prop),
})`
    svg {
        width: 100%;
        height: 100%;
        display: block;
        pointer-events: none;
    }
`

const ErrorContainer = styled(BaseContainer)`
    background-color: ${({ theme }) => theme.colors.warning};

    border-radius: 4px;
    color: ${({ theme }) => theme.colors.error};
`

const ErrorText = styled.span`
    font-size: 12px;
    font-weight: bold;
`
